import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

interface BrowserClerkOrganization {
  id?: string | null
  slug?: string | null
}

interface BrowserClerk {
  organization?: BrowserClerkOrganization | null
  session?: {
    getToken: (options?: {
      organizationId?: string
      skipCache?: boolean
    }) => Promise<string | null>
  } | null
  user?: {
    organizationMemberships?:
      | { organization: BrowserClerkOrganization }[]
      | null
  } | null
}

interface OrganizationTokenOptions {
  skipCache?: boolean
}

function getBrowserClerk(): BrowserClerk | undefined {
  if (typeof window === "undefined") {
    return undefined
  }
  return (window as typeof window & { Clerk?: BrowserClerk }).Clerk
}

/**
 * Reads the organization claims from a Clerk session token.
 *
 * The transport module verifies its own tokens the same way in
 * `transportAuthToken`, and `llm-generation` keeps a copy in
 * `activeClerkToken`; module structure keeps `@/transport` out of
 * implementation lanes and `src/shared` below `src/modules`, so this shared
 * lane checks its own rather than importing either.
 */
function readTokenOrganization(
  token: string,
): { id: string | null; slug: string | null } | null {
  const payload = token.split(".")[1]
  if (!payload) {
    return null
  }

  try {
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>
    const id = claims.org_id
    const slug = claims.org_slug
    return {
      id: typeof id === "string" ? id : null,
      slug: typeof slug === "string" ? slug : null,
    }
  } catch {
    // An opaque or malformed token cannot be checked here, so it is not
    // rejected on that basis; the server still validates it.
    return null
  }
}

function tokenMatchesOrganization(
  token: string,
  organizationId: string,
  organizationSlug: string,
): boolean {
  const claims = readTokenOrganization(token)
  if (claims === null) {
    return true
  }
  if (claims.id !== null && claims.id !== organizationId) {
    return false
  }
  return claims.slug === null || claims.slug === organizationSlug
}

/**
 * Mints a session token for one named organization.
 *
 * A bare `getToken()` signs for whichever organization the browser last
 * activated, which is one value per profile — rewritten by whichever tab
 * switched most recently — so it is only incidentally the organization a
 * caller is acting on. The organization id is resolved from the caller's slug
 * against the session's own state so the token is tied to the slug instead.
 *
 * Passing `organizationId` is not enough on its own: during a Clerk state
 * transition the cache can return a token minted for the previous
 * organization. The returned claims are compared against the requested
 * organization and the mint retried once with the cache skipped.
 *
 * Returns null outside the browser, without a session, when the session has
 * no membership in the named organization, or when the minted token keeps
 * disagreeing with it — never a token for a different organization.
 */
async function getOrganizationToken(
  organizationSlug: string,
  options: OrganizationTokenOptions = {},
): Promise<string | null> {
  const clerk = getBrowserClerk()
  const session = clerk?.session
  if (!session?.getToken) {
    return null
  }

  const activeOrganization = clerk?.organization
  const organizationId =
    activeOrganization?.slug === organizationSlug
      ? (activeOrganization?.id ?? null)
      : (clerk?.user?.organizationMemberships?.find(
          (membership) => membership.organization.slug === organizationSlug,
        )?.organization.id ?? null)

  if (organizationId === null) {
    return null
  }

  const cacheAttempts = options.skipCache ? [true] : [false, true]
  for (const skipCache of cacheAttempts) {
    const token = await session.getToken({
      organizationId,
      ...(skipCache ? { skipCache } : {}),
    })
    if (
      token &&
      tokenMatchesOrganization(token, organizationId, organizationSlug)
    ) {
      return token
    }
  }

  return null
}

/**
 * `Authorization` headers for `organizationSlug`, failing closed.
 *
 * Returns `{}` when there is no Clerk session to mint from — an
 * unauthenticated request carries no `__session` cookie either, so it fails
 * server-side regardless. Throws when a session exists but no token for the
 * organization can be minted: a live session must never fall back to the
 * browser-global cookie, which names whichever organization the last-focused
 * tab activated and may not be this one.
 */
export async function getOrganizationBearerHeaders(
  organizationSlug: string,
  options: OrganizationTokenOptions = {},
): Promise<Record<string, string>> {
  if (!getBrowserClerk()?.session?.getToken) {
    return {}
  }

  const token = await getOrganizationToken(organizationSlug, options)
  if (token === null) {
    throw new Error(
      `No session token could be minted for organization "${organizationSlug}".`,
    )
  }

  return { Authorization: `Bearer ${token}` }
}

/**
 * `getOrganizationBearerHeaders` for the organization this tab is showing.
 *
 * The organization named in the tab's URL wins — it is the only tab-local
 * signal, immune to another tab's activation. On a route that names no
 * organization (e.g. global Sofia Home at `/home`), the mint falls back to
 * Clerk's *active* organization: the page's own organization-scoped UI follows
 * the active organization on those routes, so the header matches what the tab
 * is showing — and a claim-checked token still beats riding the cookie.
 * Returns `{}` only when neither signal names an organization.
 */
export async function getRouteOrganizationBearerHeaders(
  options: OrganizationTokenOptions = {},
): Promise<Record<string, string>> {
  if (typeof window === "undefined") {
    return {}
  }

  const organizationSlug =
    getRouteOrganizationSlug(window.location.pathname) ??
    getBrowserClerk()?.organization?.slug ??
    null
  if (!organizationSlug) {
    return {}
  }

  return getOrganizationBearerHeaders(organizationSlug, options)
}
